import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import IconButton from "@/components/IconButton";
import { MdMenu } from "@/components/Menu/LitMenu";
import MenuOverlay from "@/components/Menu/MenuOverlay";
import { toMenuListEntries, type Action } from "@/components/MenuButton";
import { actionId, type TableRowMenu } from "./utils";

/**
 * A single menu that shows actions for the selected row.
 * Intended for use with side-scrolling tables.
 * A workaround for absolute positioning with `overflow: auto`.
 *
 * Like `useActionMenu`, but accepts the higher-level `Action` shape used by
 * the page-header action cluster (sections, destructive ordering, mobile
 * side-sheet) via the shared `MenuOverlay`.
 *
 * Pass `reposition` to `Table.onHorizontalScroll` so the open menu stays
 * anchored while the table scrolls horizontally — see the `SideScrollingTable`
 * story in `buildActionColumn.stories.tsx`.
 */
export default function useTableRowMenu() {
  const menuRef = useRef<MdMenu>(null);
  const [menu, setMenu] = useState<TableRowMenu>();

  // Keep the latest `menu` in a ref so callbacks below can read it without
  // re-creating themselves on every state change. Without this, `ActionCell`
  // re-identifies per click and every row's <IconButton> unmounts/remounts.
  const menuStateRef = useRef(menu);
  menuStateRef.current = menu;

  function reposition() {
    menuRef.current?.reposition();
  }

  useEffect(() => {
    document.addEventListener("scroll", reposition);
    return () => {
      document.removeEventListener("scroll", reposition);
    };
  }, []);

  useEffect(() => {
    const { current } = menuRef;
    if (!current) return;

    if (menu) {
      current.show();
    } else {
      current.close();
    }
  }, [menu]);

  const setMenu$ = useCallback((newMenu: TableRowMenu) => {
    // Re-check the underlying open state — the native `md-menu` can close
    // itself (outside click, escape) before this React state catches up.
    if (menuRef.current?.open && menuStateRef.current?.rowId === newMenu.rowId) {
      setMenu(undefined);
    } else {
      setMenu(newMenu);
    }
  }, []);

  const ActionCell = useCallback(
    ({ id, actions }: { id: string; actions: Action[] }) => {
      function onClick() {
        setMenu$({ rowId: id, actions });
      }

      return (
        <div className="flex flex-row justify-end">
          <IconButton
            id={actionId(id)}
            onClick={onClick}
            data-testid="actions"
            title="actions"
            name="more_vert"
            iconClassName="font-extrabold"
          />
        </div>
      );
    },
    [setMenu$]
  );

  const menuItems = useMemo(() => (menu ? toMenuListEntries(menu.actions) : []), [menu]);

  const closeMenu = useCallback((open: boolean) => {
    if (!open) setMenu(undefined);
  }, []);

  const Menu = useCallback(
    () => (
      <MenuOverlay
        ref={menuRef}
        menuItems={menuItems}
        open={!!menu}
        setOpen={closeMenu}
        anchorId={menu ? actionId(menu.rowId) : undefined}
        positioning="fixed"
        onClosed={() => setMenu(undefined)}
      />
    ),
    [menu, menuItems, closeMenu]
  );

  return {
    reposition: useCallback(reposition, []),
    setMenu: setMenu$,
    ActionCell,
    Menu
  };
}
